// React Flow only draws an edge when its `sourceHandle` / `targetHandle` matches a handle the
// node actually declares. Graphs produced elsewhere (the workflow generator, hand-written JSON)
// often use a generic `"default"` handle name, which matches nothing on nodes that name their
// handles — the edge then silently disappears.
//
// These helpers map an unknown handle id onto the node's real one, and leave anything that
// already resolves exactly as it was.

const single = (id) => () => [id];

/**
 * Handle ids declared by each node type, per side.
 *
 * `null` represents a handle declared without an `id` (React Flow treats it as null), which is
 * what a plain `<Handle type="target" />` exposes. The first entry is the fallback used when an
 * incoming edge names a handle that doesn't exist.
 */
export const NODE_HANDLES = {
  inputNode: { source: single("input") },
  inputDocumentNode: { source: single("document-text") },
  textOutputNode: { target: single("output") },
  llmNode: {
    target: (node) => (node?.data?.inputs ?? []).map((input) => input.id),
    source: single("llm-output"),
  },
  pythonNode: {
    target: (node) => (node?.data?.inputs ?? []).map((input) => input.id),
    source: single("output"),
  },
  dataSetNode: { target: () => [null], source: single("dataSet-input") },
  databaseNode: { target: () => [null], source: single("workflow-input") },
  chromaDatabaseNode: { target: () => [null], source: single("workflow-input") },
  workflowNode: { target: () => [null], source: single("workflow-input") },
  chromaInputNode: { target: single("chroma-input") },
  conditionalNode: {
    target: () => [null],
    source: (node) => ["default", ...(node?.data?.labels ?? [])],
  },
  generateQuestionsNode: { target: () => [null], source: single("questions") },
  textSplittingNode: { target: () => [null], source: single("document-chunks") },
  routerNode: {
    target: () => [null],
    source: (node) => (node?.data?.routes ?? []).map((route) => route.label),
  },
  webFetchNode: { target: single("web-fetch-input"), source: single("web-fetch-output") },
  sqlQueryNode: { target: single("sql-query-input"), source: single("sql-query-output") },
  defaultNode: { target: () => [null], source: () => ["a", "b"] },
};

/** Handle ids for a node on one side, including `null` for an unnamed handle. */
export const getHandleIds = (node, side) => {
  const spec = NODE_HANDLES[node?.type];
  if (!spec) return null; // unknown type — don't guess

  const entry = side === "source" ? spec.source : spec.target;
  if (!entry) return [];

  const ids = typeof entry === "function" ? entry(node) : entry;
  return Array.isArray(ids) ? ids : [];
};

/**
 * Pick the handle an edge should attach to.
 *
 * @returns {{handle: string|null, changed: boolean}} The resolved id and whether it differs.
 */
const resolveHandle = (node, side, current) => {
  const ids = getHandleIds(node, side);

  // Unknown node type, or the node exposes no handles on this side: leave the edge alone.
  if (ids === null || ids.length === 0) {
    return { handle: current, changed: false };
  }

  const wanted = current === undefined ? null : current;
  if (ids.includes(wanted)) {
    return { handle: wanted, changed: false };
  }

  return { handle: ids[0], changed: true };
};

/**
 * Rewrite edge handles that don't exist on their node.
 *
 * Pure and idempotent: a graph whose handles all resolve comes back untouched (but copied).
 *
 * @param {{nodes?: object[], edges?: object[]}} graph
 * @returns {{nodes: object[], edges: object[]}} A new graph; `changed` is exposed per edge via
 *   `edge.__handleRewritten` only internally — callers should just use the result.
 */
export const normalizeGraphHandles = (graph = {}) => {
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : [];
  const edges = Array.isArray(graph.edges) ? graph.edges : [];

  const byId = new Map(nodes.map((node) => [String(node?.id), node]));

  const normalizedEdges = edges.map((edge) => {
    if (!edge) return edge;

    const sourceNode = byId.get(String(edge.source));
    const targetNode = byId.get(String(edge.target));

    const source = resolveHandle(sourceNode, "source", edge.sourceHandle);
    const target = resolveHandle(targetNode, "target", edge.targetHandle);

    if (!source.changed && !target.changed) {
      return edge;
    }

    return {
      ...edge,
      sourceHandle: source.handle,
      targetHandle: target.handle,
    };
  });

  return { ...graph, nodes, edges: normalizedEdges };
};

export default normalizeGraphHandles;
